import { gqlClient } from '../../components/AppProvider';
import { PayoutReportItem } from '../../gql-types.generated';
import { queryPayoutReports } from '../../gql/QueryPayoutReports';
import { AppThunk } from '../../store';
import { decrementRequestsInFlight, incrementRequestsInFlight } from '../app/AppSlice';
import { clearPayoutReports, fetchPayoutReportsStart, fetchPayoutReportsSuccess, fetchPayoutReportsError } from './ReportsSlice';

/**
 * Identifies the most recent request so an earlier one cannot overwrite it.
 *
 * Year chips are one click apart and each is a round trip, so a merchant clicking 2024
 * then 2023 can have two requests open at once with no guarantee they return in order.
 * Without this, the slower 2024 response lands last and the page shows 2024's reports
 * under a selected 2023 chip - wrong data under the right label, with nothing on screen
 * to suggest it. Held outside the store because it is not state anything renders.
 */
let latestRequestId = 0;

/**
 * Loads the payout reports for one calendar year, plus the list of years that have any.
 *
 * Omit the year on first load: the server then answers with its most recent year that has
 * data, which is the year the page wants to open on, in a single round trip.
 */
export const fetchPayoutReports =
  (year?: number): AppThunk =>
  async dispatch => {
    latestRequestId += 1;
    const requestId = latestRequestId;

    // The app bar's progress bar is driven by this count, which is how every other fetch
    // in the portal reports that it is working. The page adds no spinner of its own.
    dispatch(incrementRequestsInFlight());
    dispatch(fetchPayoutReportsStart());

    try {
      const { years, reports } = await queryPayoutReports(gqlClient, year);

      if (requestId === latestRequestId) {
        dispatch(
          fetchPayoutReportsSuccess({
            years,
            reports: reports as PayoutReportItem[],
            selectedYear: year,
          }),
        );
      }
    } catch (e) {
      if (requestId === latestRequestId) {
        dispatch(fetchPayoutReportsError(e as Error));
      }
    } finally {
      // Outside the guard: a superseded request still has to give back the count it took,
      // or the progress bar never stops.
      dispatch(decrementRequestsInFlight());
    }
  };

/**
 * Clears the loaded year AND abandons any request still in flight.
 *
 * Clearing the state alone is not enough. A request opened on the way out still resolves
 * after the page has gone, and its success handler would write the reports straight back
 * into the store that was just emptied - so returning to the page would show the previous
 * visit's year for a frame before the new request replaced it. Advancing the request id
 * here is what makes that late response fail its own guard above.
 */
export const resetPayoutReports = (): AppThunk => dispatch => {
  latestRequestId += 1;

  dispatch(clearPayoutReports());
};
